import styled from '@emotion/styled';
import { PrimaryButton, TextInputField } from '@liveoptics/react-ui';
import { useRequest } from 'ahooks';
import React, { useCallback, useState } from 'react';
import { checkEmail } from '../../../../api/api';
import { Error, TheaterType, VerifyOwnerState } from '../../../../enums';
import { EmailCheckResponse, RegisterNewUserRequest } from '../../../../interfaces';
import TextButton from '../../../buttons/TextButton';
import SelectionOption from '../SelectionOption';
import Form from '../form/Form';
import ExistingCustomerForm from './ExistingCustomerForm';
import NewCustomerForm from './NewCustomerForm';

const EmailContainer = styled.div`
  margin-bottom: 10px;
`;

export type CustomerFormProps = {
  projectId: number;
  defaultTheater: TheaterType;
  emailCheckResponse?: EmailCheckResponse;
  registerNewUserRequest?: RegisterNewUserRequest;
  verifyOwnerStateCallback: (state: VerifyOwnerState, value?: unknown) => void;
  onOptionChange: (option: SelectionOption) => void;
  onCancel: () => void;
  onSuccess: (state: VerifyOwnerState, email?: string) => void;
};

const CustomerForm = (props: CustomerFormProps) => {
  const [email, setEmail] = useState<string>(
    props.registerNewUserRequest?.Email ?? props.emailCheckResponse?.Email ?? ''
  );
  const [isValidEmail, setIsValidEmail] = useState<boolean>(
    props.registerNewUserRequest?.Email || props.emailCheckResponse?.Email ? true : false
  );
  const [isEmailRoleInvalid, setIsEmailRoleInvalid] = useState<boolean | undefined>(undefined);
  const [showExistingCustomer, setShowExistingCustomer] = useState<boolean | undefined>(
    props.emailCheckResponse ? true : undefined
  );
  const [showNewCustomer, setShowNewCustomer] = useState<boolean | undefined>(
    props.registerNewUserRequest ? true : undefined
  );
  const [error, setError] = useState('');

  const checkEmailRequest = useRequest(checkEmail, {
    manual: true,
    debounceWait: 500,
  });

  const handleEmailChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      setEmail(value);
      setIsValidEmail(validEmail);
      setShowExistingCustomer(undefined);
      setShowNewCustomer(undefined);
      setIsEmailRoleInvalid(undefined);
      setError('');
      props.verifyOwnerStateCallback(VerifyOwnerState.SelectOwner);
    },
    [props]
  );

  const handleNextClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      setError('');
      checkEmailRequest
        .runAsync(email)
        .then((r) => {
          const isEmailRoleInvalid = r && r.Data.data.ExistingUserFoundNotEndUser;
          setIsEmailRoleInvalid(isEmailRoleInvalid);
          if (isEmailRoleInvalid) setIsEmailRoleInvalid(isEmailRoleInvalid);
          else if (r && r.Data.data.ExistingAccountId !== 0) {
            setShowExistingCustomer(true);
          } else if (r && r.Data.data.ExistingAccountId === 0) {
            setShowNewCustomer(true);
          }
        })
        .catch(() => {
          setError(Error.EmailCheck);
        });
    },
    [checkEmailRequest, email]
  );

  if (showExistingCustomer && checkEmailRequest.data) {
    return (
      <ExistingCustomerForm
        projectId={props.projectId}
        email={email}
        emailCheckResponse={{ ...checkEmailRequest.data.Data.data, Email: email }}
        defaultTheater={props.defaultTheater}
        verifyOwnerStateCallback={props.verifyOwnerStateCallback}
        onOptionChange={props.onOptionChange}
        onEmailChange={handleEmailChange}
        onCancel={props.onCancel}
        onSuccess={props.onSuccess}
      />
    );
  }
  if (showExistingCustomer && props.emailCheckResponse) {
    return (
      <ExistingCustomerForm
        projectId={props.projectId}
        email={email}
        emailCheckResponse={props.emailCheckResponse}
        defaultTheater={props.defaultTheater}
        verifyOwnerStateCallback={props.verifyOwnerStateCallback}
        onOptionChange={props.onOptionChange}
        onEmailChange={handleEmailChange}
        onCancel={props.onCancel}
        onSuccess={props.onSuccess}
      />
    );
  }
  if (showNewCustomer)
    return (
      <NewCustomerForm
        projectId={props.projectId}
        email={email}
        defaultTheater={props.defaultTheater}
        registerNewUserRequest={props.registerNewUserRequest}
        onCancel={props.onCancel}
        onOptionChange={props.onOptionChange}
        onEmailChange={handleEmailChange}
        verifyOwnerStateCallback={props.verifyOwnerStateCallback}
        onSuccess={props.onSuccess}
      />
    );

  return (
    <Form
      selectionOption={SelectionOption.Customer}
      error={isEmailRoleInvalid ? Error.UserRoleCheck : error ?? ''}
      renderActions={() => (
        <>
          <TextButton onClick={props.onCancel}>Cancel</TextButton>
          <PrimaryButton
            disabled={!isValidEmail || checkEmailRequest.loading}
            loading={checkEmailRequest.loading}
            loadingText="Loading..."
            clickFn={handleNextClick}
          >
            Next
          </PrimaryButton>
        </>
      )}
      onChange={props.onOptionChange}
    >
      <EmailContainer>
        <TextInputField
          label=""
          autoFocus={true}
          autoComplete="on"
          type="email"
          placeholder="Enter email address"
          value={email}
          status={email === '' || isValidEmail ? 'active' : 'invalid'}
          validationMessage={'Please enter a valid customer email address'}
          onChange={handleEmailChange}
        />
      </EmailContainer>
    </Form>
  );
};

export default CustomerForm;
